/**
 * Example Questions Panel
 * =======================
 * Reusable component showing clickable example questions for agents
 */

import React from 'react';
import { MessageSquare, Sparkles } from 'lucide-react';

interface ExampleQuestionsProps {
  questions: string[];
  onQuestionClick?: (question: string) => void;
  title?: string;
  description?: string;
  accentColor?: string;
}

export default function ExampleQuestions({ 
  questions, 
  onQuestionClick,
  title = "Try These Questions",
  description = "Click any question to get started",
  accentColor = "purple"
}: ExampleQuestionsProps) {
  const colorClasses = {
    purple: 'border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700',
    blue: 'border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700',
    emerald: 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700',
    pink: 'border-pink-200 bg-pink-50 hover:bg-pink-100 text-pink-700',
    red: 'border-red-200 bg-red-50 hover:bg-red-100 text-red-700',
    indigo: 'border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700',
    rose: 'border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700',
    yellow: 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100 text-yellow-700',
    orange: 'border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700',
  };

  const borderColor = colorClasses[accentColor as keyof typeof colorClasses] || colorClasses.purple;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-amber-100 rounded-lg">
          <Sparkles className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {questions.map((question, idx) => (
          <button
            key={idx}
            onClick={() => onQuestionClick?.(question)}
            className={`
              text-left p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-md
              ${borderColor}
            `}
          >
            <div className="flex items-start gap-3">
              <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70" />
              <span className="text-sm font-medium">{question}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

